import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Modal, Table, Tag, Button, Space, Typography, Alert, Spin, Popconfirm,
  Progress, Empty, Badge, message
} from "antd";
import {
  MergeCellsOutlined, WarningOutlined, SwapOutlined,
  CheckCircleOutlined, InfoCircleOutlined
} from "@ant-design/icons";
import { getDuplicateLeadsThunk, mergeLeadsThunk } from "../../../Slices/salesSlice";

const { Text } = Typography;

const SCORE_COLOR = (score) => {
  if (score >= 70) return "#f5222d";
  if (score >= 50) return "#fa8c16";
  return "#1890ff";
};

const DuplicateLeadMerge = ({ visible, onClose, onMerged }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(null); // "primaryId|dupId" while merging
  const [groups, setGroups] = useState([]);
  const [totalGroups, setTotalGroups] = useState(0);

  useEffect(() => {
    if (visible) loadDuplicates();
  }, [visible]);

  const loadDuplicates = async () => {
    setLoading(true);
    try {
      const result = await dispatch(getDuplicateLeadsThunk()).unwrap();
      if (result?.status === 200) {
        setGroups(result.groups || []);
        setTotalGroups(result.totalGroups || 0);
      } else {
        message.error(result?.message || "Failed to load duplicates");
      }
    } catch (err) {
      message.error(err || "Failed to scan for duplicates");
    }
    setLoading(false);
  };

  const handleMerge = async (primaryId, duplicateId, primaryName, dupName) => {
    setMerging(`${primaryId}|${duplicateId}`);
    try {
      const result = await dispatch(
        mergeLeadsThunk({ primaryLeadId: primaryId, duplicateLeadId: duplicateId })
      ).unwrap();
      if (result?.status === 200) {
        message.success(result.message);
        // Remove the merged duplicate from groups
        setGroups((prev) =>
          prev
            .map((g) => ({
              ...g,
              matches: g.matches.filter((m) => m.id !== duplicateId),
            }))
            .filter((g) => g.matches.length > 0)
        );
        setTotalGroups((prev) => Math.max(0, prev - 1));
        if (onMerged) onMerged();
      } else {
        message.error(result?.message || "Merge failed");
      }
    } catch (err) {
      message.error(err || "Merge failed");
    }
    setMerging(null);
  };

  const matchColumns = [
    {
      title: "Lead #",
      dataIndex: "leadID",
      width: 70,
      render: (id) => <Text strong>#{id}</Text>,
    },
    {
      title: "Agency Name",
      dataIndex: "agencyName",
      ellipsis: true,
    },
    {
      title: "Location",
      render: (_, r) =>
        [r.city, r.state].filter(Boolean).join(", ") || "-",
      width: 120,
    },
    {
      title: "Assigned To",
      dataIndex: "assignedTo",
      width: 110,
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 90,
      render: (s) => <Tag>{s || "N/A"}</Tag>,
    },
    {
      title: "Activity",
      width: 120,
      render: (_, r) => (
        <Space size={4}>
          <Tag color="blue">{r.contactCount} contacts</Tag>
          <Tag color="green">{r.callCount} calls</Tag>
          <Tag color="purple">{r.emailCount} emails</Tag>
        </Space>
      ),
    },
    {
      title: "Match",
      width: 140,
      render: (_, r) => (
        <div>
          <Progress
            percent={Math.min(r.matchScore, 100)}
            size="small"
            strokeColor={SCORE_COLOR(r.matchScore)}
            format={() => `${r.matchScore}%`}
            style={{ width: 80 }}
          />
          <div style={{ fontSize: 10, color: "#888" }}>{r.matchReason}</div>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <MergeCellsOutlined />
          <span>Duplicate Lead Detection & Merge</span>
          {totalGroups > 0 && (
            <Badge count={totalGroups} style={{ backgroundColor: "#f5222d" }} />
          )}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={loadDuplicates} loading={loading}>
            Re-scan
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
      width={1000}
      styles={{ body: { maxHeight: "65vh", overflowY: "auto" } }}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: "#888" }}>
            Scanning leads for duplicates...
          </div>
        </div>
      ) : groups.length === 0 ? (
        <Empty
          image={<CheckCircleOutlined style={{ fontSize: 48, color: "#52c41a" }} />}
          description={
            <span>
              <Text strong>No duplicates found!</Text>
              <br />
              <Text type="secondary">Your leads database looks clean.</Text>
            </span>
          }
        />
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            message={`Found ${totalGroups} potential duplicate group${totalGroups > 1 ? "s" : ""}. Review each group and merge duplicates into the primary lead.`}
            style={{ marginBottom: 12 }}
          />

          {groups.map((group, gIdx) => (
            <div
              key={group.primaryId}
              style={{
                marginBottom: 16,
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "8px 12px",
                  background: "#fafafa",
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Space>
                  <Tag color="green">PRIMARY</Tag>
                  <Text strong>{group.primaryName}</Text>
                </Space>
              </div>

              <Table
                size="small"
                dataSource={group.matches}
                columns={[
                  ...matchColumns,
                  {
                    title: "Action",
                    width: 100,
                    fixed: "right",
                    render: (_, record) => (
                      <Popconfirm
                        title="Merge this duplicate?"
                        description={
                          <div style={{ maxWidth: 300 }}>
                            <WarningOutlined style={{ color: "#fa8c16", marginRight: 4 }} />
                            This will merge <strong>{record.agencyName}</strong>'s contacts,
                            calls, emails, and notes into <strong>{group.primaryName}</strong>,
                            then <strong style={{ color: "#f5222d" }}>delete</strong> the duplicate.
                            This cannot be undone.
                          </div>
                        }
                        onConfirm={() =>
                          handleMerge(group.primaryId, record.id, group.primaryName, record.agencyName)
                        }
                        okText="Yes, Merge"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          size="small"
                          type="primary"
                          danger
                          icon={<SwapOutlined />}
                          loading={merging === `${group.primaryId}|${record.id}`}
                        >
                          Merge
                        </Button>
                      </Popconfirm>
                    ),
                  },
                ]}
                rowKey="id"
                pagination={false}
              />
            </div>
          ))}
        </>
      )}
    </Modal>
  );
};

export default DuplicateLeadMerge;
